import { NgIf } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router, RouterLink } from "@angular/router";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../../shared/services/translations/language.service';
import { ApiErrorService } from '../../../../../shared/services/api-error.service';
import { AuthApiService } from '../../../services/auth-api.service';
import { ToastNotificationType, ToastPosition } from '../../../../../notifications/models/toast-notification-type';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';

@Component({
  selector: 'app-new-password',
  imports: [RouterLink, NgIf, TranslateModule],
  templateUrl: './new-password.component.html',
  styleUrl: './new-password.component.css'
})
export class NewPasswordComponent {
  showPassword = false;
  showConfirmPassword = false;
  loading = false;
  errorMessage = '';
  translate = inject(TranslateService);
  languageService = inject(LanguageService);
  private apiErrorService = inject(ApiErrorService);
  private authApiService = inject(AuthApiService);
  private router = inject(Router);
  toastNotificationService = inject(ToastNotificationService);

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPassword() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  submit(password: string, confirmPassword: string, event: Event) {

    event.preventDefault();

    this.errorMessage = '';

    if (!password || !confirmPassword) {
      this.errorMessage = this.translate.instant('auth.new_password.errors.fields_required');
      return;
    }

    if (password.length < 8) {
      this.errorMessage = this.translate.instant('auth.new_password.errors.password_too_short');
      return;
    }

    if (password !== confirmPassword) {
      this.errorMessage = this.translate.instant('auth.new_password.errors.passwords_do_not_match');
      return;
    }

    const resetToken = sessionStorage.getItem('reset_token');

    if (!resetToken) {
      this.errorMessage = this.translate.instant('auth.new_password.errors.invalid_reset_token');
      this.loading = false;
      return;
    }

    this.loading = true;

    this.authApiService.confirmPasswordReset({
      reset_token: resetToken,
      new_password: password
    }).subscribe({
      next: () => {
        sessionStorage.removeItem('reset_token');
        this.toastNotificationService.createNotification(this.translate.instant('auth.new_password.toast_success_message'), ToastNotificationType.SUCCESS, this.translate.instant('auth.reset_password.toast_success_title'), ToastPosition.TOP_RIGHT);
        this.router.navigate(['/login'], {
          queryParams: {
            passwordReset: 'success'
          }
        });
      },
      error: (error) => {
        // A weak password comes back as VALIDATION_FAILED on `new_password`;
        // RESET_TOKEN_INVALID / RESET_TOKEN_EXPIRED carry a bilingual message.
        const fieldMessages = Object.values(this.apiErrorService.getFieldErrors(error)).flat();
        this.errorMessage =
          fieldMessages[0] ??
          this.apiErrorService.getDisplayMessage(
            error,
            this.translate.instant('auth.new_password.errors.reset_failed')
          );

        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });


  }
}
